import { useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { sorted } from "helpers";
import { LabellerTimeline, LabellerDetails } from "src/store/api/generatedApi";
import { ExpandedOriginalEvent } from "../types";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { expandOriginalEvent } from "components/manualReview/utils/eventUtils";

export interface LabellerPeriodWithExpandedEvents {
  periodId: number;
  periodIds: number[];
  start: Dayjs;
  end: Dayjs;
  events: ExpandedOriginalEvent[];
}

export interface Lane {
  labellerDetails: LabellerDetails;
  periods: LabellerPeriodWithExpandedEvents[];
}

export const useTimelinesByLabeller = (
  labellerTimelines: LabellerTimeline[],
  mergedEvents: ScrapEyesEventWithReviewData[],
  location: string | undefined
): Lane[] => {
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvents(location);
  return useMemo(() => {
    const eventMeta = new Map<
      number,
      { mergedId: string; hasConflict: boolean }
    >();

    mergedEvents.forEach((mergedEvent) => {
      const hasConflict = mergedEvent.review_reasons.length > 0;
      mergedEvent.original_events.forEach((originalEvent) => {
        eventMeta.set(originalEvent.id, {
          mergedId: mergedEvent.id,
          hasConflict,
        });
      });
    });

    const lanes: Lane[] = labellerTimelines.map((timeline) => ({
      labellerDetails: timeline.labeller,
      periods: timeline.periods.map((period) => ({
        periodId: period.period_id,
        periodIds: [period.period_id],
        start: dayjs(period.start),
        end: dayjs(period.end),
        events: withAreaLabels(
          sorted(
            period.events.map((event) => {
              const meta = eventMeta.get(event.id);
              return expandOriginalEvent(
                event,
                defaultAreas,
                timeline.labeller,
                meta?.mergedId,
                meta?.hasConflict
              );
            }),
            (e) => e.start.valueOf(),
            true
          )
        ),
      })),
    }));

    return sorted(
      lanes,
      (lane) => lane.labellerDetails.name ?? lane.labellerDetails.id,
      true
    );
  }, [labellerTimelines, mergedEvents, defaultAreas, withAreaLabels]);
};
